import { useState } from 'react';

import { Schemas } from '@api-client/generated/types';
import { useNewsletterController_getCampaigns } from '@api-client/generated/NewsletterController/getCampaigns';
import { useNewsletterController_getStats } from '@api-client/generated/NewsletterController/getStats';

export type NewsletterCampaign = Schemas.CampaignSummaryDto;
export type NewsletterCampaignStats = Schemas.CampaignStatsDto;

/**
 * Data layer for the campaign stats panel (plan §6.4/§15): the read-only
 * Listmonk campaign list that feeds the selector (`GET …/campaigns`) plus the
 * combined stats for the selected campaign (`GET …/stats/:campaignId` — send
 * count from Listmonk, engagement from Postmark keyed by `X-PM-Tag`).
 *
 * There is no campaign CRUD here by design: composing and sending stay in
 * Listmonk (§13). The newest campaign is selected by default — that's the one a
 * marketer just sent and wants numbers for — until they pick another.
 */
export const useNewsletterStats = () => {
  const campaignsQuery = useNewsletterController_getCampaigns();
  const [pickedId, setPickedId] = useState<number | null>(null);

  // Newest first: Listmonk's ordering isn't part of the contract, so sort here
  // rather than trusting the response order for the default selection.
  const campaigns = [...(campaignsQuery.data ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt)
  );
  const selectedId = pickedId ?? campaigns[0]?.id ?? null;

  const statsQuery = useNewsletterController_getStats({
    // Only read when a campaign exists — the query is disabled otherwise, so the
    // placeholder id is never requested.
    params: { campaignId: selectedId ?? 0 },
    config: { enabled: selectedId !== null },
  });

  return {
    campaigns,
    selectedId,
    selectCampaign: setPickedId,
    isLoadingCampaigns: campaignsQuery.isLoading,
    campaignsError: campaignsQuery.error,
    refetchCampaigns: campaignsQuery.refetch,

    stats: statsQuery.data ?? null,
    // Covers the first load and a switch to another campaign (stale data shown
    // would otherwise be attributed to the newly selected campaign).
    isLoadingStats: selectedId !== null && statsQuery.isFetching,
    statsError: statsQuery.error,
  };
};
